import { AuthApi } from '@/src/api'
import { PATIENT_SIDEBAR_FOOTER_ITEMS, PATIENT_SIDEBAR_ITEMS, PatientSidebarIconKey } from '@data'
import { useGuardedNavigate } from '@hook'
import { NewLogo } from '@icons'
import { getHospitalName } from '@util'
import { ReactNode } from 'react'
import { FiCalendar, FiChevronDown, FiFolder, FiHome, FiLogOut, FiMessageCircle, FiSearch } from 'react-icons/fi'
import { LuPill } from 'react-icons/lu'
import { RiCustomerService2Line } from 'react-icons/ri'
import { NavLink } from 'react-router-dom'

const iconMap: Record<PatientSidebarIconKey, ReactNode> = {
  appointments: <FiCalendar size={18} />,
  charts: <FiFolder size={18} />,
  dashboard: <FiHome size={18} />,
  logout: <FiLogOut size={18} />,
  medications: <LuPill size={18} />,
  messages: <FiMessageCircle size={18} />,
  support: <RiCustomerService2Line size={18} />
}

const PatientSidebar = () => {
  const hospitalName = getHospitalName()
  const { guardedNavigate } = useGuardedNavigate()

  const { logout } = AuthApi()
  return (
    <aside className="flex h-full w-full md:w-[252px] flex-col justify-between border-[#E9E9E9]">
      <div className="flex flex-1 flex-col overflow-auto px-3 pb-6 pt-7">
        <NewLogo />

        <div className="mt-6 rounded-xl border border-[#E7E7E7] px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#53B438] text-xs font-semibold text-white">
              {hospitalName}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[20px] text-[#1A1A1A]">{hospitalName}</p>
              <p className="text-xs text-[#8A8A8A]">Tap the drop down to switch</p>
            </div>
            <FiChevronDown className="text-[#A3A3A3]" size={18} />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-xl border border-[#E5E5E5] px-3 py-2.5 text-[#999999]">
          <FiSearch size={16} />
          <input
            className="w-full bg-transparent text-sm text-[#464646] placeholder:text-[#B3B3B3] outline-none"
            placeholder="Search"
            type="text"
          />
        </div>

        <nav className="mt-5 flex flex-col gap-1">
          {PATIENT_SIDEBAR_ITEMS.map(item => (
            <NavLink
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-base transition-colors ${
                  isActive ? 'bg-[#EDF0F6] text-[#1B1B1B]' : 'text-[#737373] hover:bg-[#F7F7F7]'
                }`
              }
              key={item.id}
              onClick={event => {
                event.preventDefault()
                guardedNavigate(item.path)
              }}
              to={item.path}
            >
              <span className="text-current">{iconMap[item.icon]}</span>
              <span className="flex-1">{item.label}</span>
              {item.badge ? (
                <span className="rounded-md bg-[#F0F0F0] px-1.5 py-0.5 text-xs font-medium text-[#555555]">
                  {item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="border-t border-[#ECECEC] px-3 py-4">
        <div className="flex flex-col gap-1">
          {PATIENT_SIDEBAR_FOOTER_ITEMS.map(item => (
            <NavLink
              className="flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-[#737373] transition-colors hover:bg-[#F7F7F7]"
              key={item.id}
              onClick={event => {
                event.preventDefault()
                if (item.id === 'logout') {
                  logout()
                } else {
                  guardedNavigate(item.path)
                }
              }}
              to={item.path}
            >
              <span>{iconMap[item.icon]}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </aside>
  )
}

export default PatientSidebar
